import React, { useEffect, useMemo, useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import Avatar from 'shared/components/ui/Avatar';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import { drawerIconButtonClass } from 'features/activity/styles/dataTableStyles';

import { listEditors, addEditor, removeEditor } from 'features/prompts/api/promptsApi';
import { memberById } from 'features/prompts/lib/promptMembers';
import BrandSelect from 'features/playground/components/BrandSelect';

/** Avatar-ish initial + name/email row. */
function MemberRow({ name, email, avatar, right }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-[color:var(--border-subtle)] py-2 last:border-b-0">
      <Avatar name={name || email} src={avatar} size={24} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] text-[color:var(--text-primary)]">{name || email || 'Unknown'}</div>
        {name && email && <div className="truncate text-[11px] text-[color:var(--text-muted)]">{email}</div>}
      </div>
      {right}
    </div>
  );
}

/**
 * Per-prompt access panel. Everyone on the team can VIEW; this controls who can
 * EDIT. The prompt's creator is the owner (always editable, shown with a crown);
 * the owner can invite/revoke other team members as editors.
 */
export default function MembersPanel({ promptId, ownerId, members, isOwner, onClose }) {
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    listEditors(promptId)
      .then((r) => { if (live) setEditors(r.editors); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [promptId]);

  const owner = memberById(ownerId, members);

  // Team members who could still be invited (not the owner, not already editors).
  const invitable = useMemo(() => {
    const editorIds = new Set(editors.map((e) => Number(e.user_id)));
    return (members || []).filter(
      (m) => Number(m.userId) !== Number(ownerId) && !editorIds.has(Number(m.userId)),
    );
  }, [members, ownerId, editors]);

  const invite = async (userId) => {
    if (!userId) return;
    setBusy(true); setError(null);
    try {
      const next = await addEditor(promptId, { userId: Number(userId) });
      setEditors(next);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const revoke = async (userId) => {
    setBusy(true); setError(null);
    try {
      const next = await removeEditor(promptId, userId);
      setEditors(next);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-[420px] max-w-[92vw] rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between border-b border-[color:var(--border-subtle)] pb-2">
          <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">Who can edit</h2>
          <button type="button" onClick={onClose} aria-label="Close" title="Close" className={drawerIconButtonClass}><X size={15} /></button>
        </div>

        {error && <div className="mb-2 rounded-md border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-2.5 py-1.5 text-[11.5px] text-[color:var(--feedback-error)]">{error}</div>}

        {/* owner */}
        <MemberRow
          name={owner?.name}
          email={owner?.email}
          avatar={owner?.avatar_url}
          right={<span className="text-[11px] text-[color:var(--text-muted)]">Owner</span>}
        />

        {/* editors */}
        {loading ? (
          <div className="py-3"><PageLoader label="Loading editors" /></div>
        ) : (
          editors.map((e) => {
            const m = memberById(e.user_id, members);
            return (
              <MemberRow
                key={e.user_id}
                name={m?.name}
                email={m?.email || e.email}
                avatar={m?.avatar_url}
                right={isOwner ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => revoke(e.user_id)}
                    title="Revoke edit access"
                    aria-label="Revoke edit access"
                    className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--feedback-error)] disabled:opacity-40"
                  >
                    <Trash2 size={13} />
                  </button>
                ) : <span className="text-[11px] text-[color:var(--text-muted)]">Editor</span>}
              />
            );
          })
        )}

        {/* invite (owner only) */}
        {isOwner && (
          <div className="mt-3 pt-1">
            {invitable.length === 0 ? (
              <div className="text-[12px] text-[color:var(--text-muted)]">Everyone on the team already has edit access.</div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <BrandSelect
                    value=""
                    placeholder="Invite a teammate…"
                    disabled={busy}
                    minWidth={0}
                    options={invitable.map((m) => ({ value: String(m.userId), label: m.name || m.email }))}
                    onChange={(v) => invite(v)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
